<template>
  <v-main>
    <header-components></header-components>
    <title-components></title-components>
    <v-container
      class="mx-auto mt-6"
      style="max-width: 1200px;"
    >
      <progress-components page="time"></progress-components>
      <v-form ref="form" class="mt-12">
        <interview-link></interview-link>
        <v-card
          class="mx-auto mt-1"
          elevation="1"
          outlined
        >
          <v-card-title>
            <strong>担当医師名</strong><span class="text-red"><strong> *</strong></span>
          </v-card-title>
          <v-responsive
            class="mx-5"
            max-width="344"
          >
            <v-text-field
              label="回答を入力"
              variant="underlined"
              :rules="[required]"
              v-model="doctorName"
            ></v-text-field>
          </v-responsive>
        </v-card>
        <v-card
          class="mx-auto mt-6"
          elevation="1"
          outlined
        >
          <v-card-title>
            <strong>実施日 </strong><span class="text-red"><strong> *</strong></span>
          </v-card-title>
          <v-responsive
            class="mx-5"
            max-width="344"
          >
            <v-text-field
              v-model="implementationDate"
              variant="underlined"
              type="date"
              :rules="[required]"
            ></v-text-field>
          </v-responsive>
        </v-card>
        <v-card
          class="mx-auto mt-6"
          elevation="1"
          outlined
        >
          <v-card-title>
            <strong>診療開始時刻 </strong><span class="text-red"><strong> *</strong></span>
          </v-card-title>
          <div class="mx-5 mb-5">※患者様毎ごとの開始時刻をご記入ください。</div>
          <v-responsive
            class="mx-5"
            max-width="344"
          >
            <v-text-field
              v-model="startTime"
              variant="underlined"
              type="time"
              :rules="[required]"
            ></v-text-field>
          </v-responsive>
        </v-card>
        <v-card
          class="mx-auto mt-6"
          elevation="1"
          outlined
        >
          <v-card-title>
            <strong>診察終了時刻 </strong><span class="text-red"><strong> *</strong></span>
          </v-card-title>
          <div class="mx-5 mb-5">※患者様毎ごとの終了時刻をご記入ください。</div>
          <v-responsive
            class="mx-5"
            max-width="344"
          >
            <v-text-field
              v-model="endTime"
              variant="underlined"
              type="time"
              :rules="[required, consultationEndTime(startTime, endTime)]"
            ></v-text-field>
          </v-responsive>
        </v-card>
      </v-form>
    </v-container>
    <v-container class="mx-auto mt-8">
      <v-row justify="center" align-content="center" class="mb-6">
        <v-btn
            x-large min-width="362px"
            color="doorAccentColor"
            class="mb-4 white--text"
            @click="clickedNextPage"
        >
          <span v-if='fromConfirm'>確認</span>
          <span v-else>次へ</span>
        </v-btn>
      </v-row>
    </v-container>
    <footer-components></footer-components>
    <v-dialog
      v-model="errorModal"
      width="400"
    >
      <v-card>
        <v-card-title class="bg-doorMainColor">エラー</v-card-title>
        <v-card-text style="margin: 1rem auto;">
          <div>通信エラーが発生しました。</div>
          <div>URLをお確かめの上再度アクセスしてください。</div>
        </v-card-text>
        <v-divider></v-divider>
        <v-spacer></v-spacer>
        <v-row justify="center" align-content="center" class="my-3">
            <v-btn
              dark
              color="doorAccentColor"
              @click="errorModal = false"
            >
            閉じる
            </v-btn>
        </v-row>
      </v-card>
    </v-dialog>
  </v-main>
</template>
<script>
import { defineComponent, computed, ref, onMounted } from 'vue'
import { Logger } from 'aws-amplify'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import HeaderComponents from '@/components/InterviewHeader.vue'
import FooterComponents from '@/components/InterviewFooter.vue'
import TitleComponents from '@/components/MedicalRecordTitle.vue'
import ProgressComponents from '@/components/MedicalRecordProgress.vue'
import InterviewLink from '@/components/InterviewLink.vue'
import { useScreenOperation } from '@/func/useScreenOperation'
import { useFormValidator } from '@/func/useFormValidator'
import { useApiParams } from '@/func/useApiParams';

const logger = new Logger('AdvanceForm');

export default defineComponent({
  components: {
    'header-components': HeaderComponents,
    'footer-components': FooterComponents,
    'title-components': TitleComponents,
    'progress-components': ProgressComponents,
    'interview-link': InterviewLink
  },
  setup() {
    const { scrollToTop, fromConfirmScreen, scrollToErrorPosition } = useScreenOperation()
    const fromConfirm = computed(() => fromConfirmScreen())
    const { required, consultationEndTime } = useFormValidator()
    const form = ref(null)
    const errorModal = ref(false)
    logger.info('MedicalRecord Time')

    const store = useStore()
    onMounted(async () => {
      await useApiParams().save()
      const treatmentConsultationSeq = store.getters["apiParams/treatmentConsultationSeq"]
      const jwtToken = store.getters["apiParams/jwtToken"]
      try {
        await store.dispatch('advanceInterview/getInterview', { treatmentConsultationSeq, jwtToken })
        const registeredInterview = await store.getters["advanceInterview/name"] !== ''
        await store.dispatch('medicalRecord/setRegisteredInterview', registeredInterview )
      } catch(e) {
        errorModal.value = true
      }
    }),
    scrollToTop()
    const doctorName = computed({
      get: () => store.state.medicalRecord.doctorName,
      set: (value) => store.dispatch('medicalRecord/setDoctorName', value)
    })
    const implementationDate = computed({
      get: () => store.state.medicalRecord.implementationDate,
      set: (value) => store.dispatch('medicalRecord/setImplementationDate', value)
    })
    const startTime = computed({
      get: () => store.state.medicalRecord.startTime,
      set: (value) => store.dispatch('medicalRecord/setStartTime', value)
    })
    const endTime = computed({
      get: () => store.state.medicalRecord.endTime,
      set: (value) => store.dispatch('medicalRecord/setEndTime', value)
    })

    const router = useRouter()
    const clickedNextPage = async () => {
      const result = await form.value.validate()
      if (!result.valid) {
        scrollToErrorPosition()
        return
      }
      if (fromConfirm.value) {
        router.push('/record/confirm')
      } else {
        router.push('/record/patient')
      }
    }
    return {
      fromConfirm,
      clickedNextPage,
      form,
      required,
      doctorName,
      implementationDate,
      startTime,
      endTime,
      errorModal,
      consultationEndTime
    }
  }
})
</script>
